import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { Search } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import { formatAuditTime } from '../services/auditTime';
import {
  apiMessage,
  BAND_OPTIONS,
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  listAuditRuns,
  parseLocalDateParam,
  STATUS_OPTIONS,
  tenantDisplayName,
  tenantOptionLabel,
} from '../services/jobAuditApi';
import HeaderHelp from './HeaderHelp';
import { serviceTitanJobUrl } from './serviceTitanJob';
import TechnicianScoreGauge from './TechnicianScoreGauge';

const PAGE_SIZE = 50;

const formatStatus = (status) => {
  if (!status) return '—';
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const statusTone = (status) => {
  if (status === 'critical_failure') return 'bg-red-100 text-red-800';
  if (status === 'human_review_required') return 'bg-purple-100 text-purple-800';
  if (status === 'technician_correction_required') return 'bg-amber-100 text-amber-800';
  if (status === 'accepted' || status === 'accepted_with_warnings') {
    return 'bg-green-100 text-green-800';
  }
  return 'bg-gray-100 text-gray-700';
};

const JobAuditQueue = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const status = searchParams.get('status') || '';
  const band = searchParams.get('band') || '';
  const job = searchParams.get('job') || '';
  const rangeParam = searchParams.get('range') || '';
  const on = searchParams.get('on') || '';
  const rangeSelect = createdRangeValue(rangeParam, on);
  const range = createdRangeQuery(rangeParam, on);
  const offset = Math.max(0, parseInt(searchParams.get('offset') || '0', 10) || 0);
  const onDate = parseLocalDateParam(on);

  const [jobDraft, setJobDraft] = useState(job);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, items: [], tenants: [] });

  useEffect(() => {
    setJobDraft(job);
  }, [job]);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (!('offset' in patch)) next.delete('offset');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const bounds = createdWindow({ range, on });
    listAuditRuns({
      tenant,
      status,
      band,
      job,
      created_after: bounds.created_after || '',
      created_before: bounds.created_before || '',
      limit: PAGE_SIZE,
      offset,
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load audit runs'));
          setData({ total: 0, items: [], tenants: [] });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, status, band, job, range, on, offset]);

  const showingFrom = data.total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + (data.items?.length || 0), data.total);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Audits</h1>
        <p className="mt-1 text-sm text-gray-600">
          Scored Home Health Report Card runs. Open a row for findings, photos, and review.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow"
        onSubmit={(e) => {
          e.preventDefault();
          setFilter({ job: jobDraft.trim() });
        }}
      >
        <label className="text-sm text-gray-700">
          Tenant
          <select
            className="mt-1 block w-56 rounded-md border-gray-300 shadow-sm"
            value={tenant}
            onChange={(e) => setFilter({ tenant: e.target.value })}
          >
            <option value="">all</option>
            {(data.tenants || []).map((option) => (
              <option key={option.code || option} value={option.code || option}>
                {tenantOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Status
          <select
            className="mt-1 block w-64 rounded-md border-gray-300 shadow-sm"
            value={status}
            onChange={(e) => setFilter({ status: e.target.value })}
          >
            <option value="">all</option>
            {STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Band
          <select
            className="mt-1 block w-36 rounded-md border-gray-300 shadow-sm"
            value={band}
            onChange={(e) => setFilter({ band: e.target.value })}
          >
            <option value="">all</option>
            {BAND_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Created
          <select
            className="mt-1 block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            value={rangeSelect}
            onChange={(e) => setFilter({ range: e.target.value, on: '' })}
          >
            <option value={ALL_CREATED_RANGE}>all</option>
            {CREATED_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Date
          <DatePicker
            selected={onDate}
            onChange={(date) => setFilter({ on: date ? formatLocalDate(date) : '', range: '' })}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="pick a day"
            wrapperClassName="mt-1 block"
            className="block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
          />
        </label>
        <label className="text-sm text-gray-700">
          Job
          <span className="mt-1 flex">
            <input
              className="block w-48 rounded-l-md border-gray-300 shadow-sm"
              value={jobDraft}
              onChange={(e) => setJobDraft(e.target.value)}
              placeholder="full or partial job id"
            />
            <button
              type="submit"
              className="inline-flex items-center rounded-r-md bg-gray-800 px-3 text-white"
            >
              <Search className="h-4 w-4" />
            </button>
          </span>
        </label>
      </form>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
          <span>
            {loading
              ? 'Loading…'
              : `Showing ${showingFrom}–${showingTo} of ${data.total}`}
          </span>
          <div className="space-x-2">
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset === 0 || loading}
              onClick={() => setFilter({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset + PAGE_SIZE >= data.total || loading}
              onClick={() => setFilter({ offset: String(offset + PAGE_SIZE) })}
            >
              Next
            </button>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Tenant</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Job</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Score
                  <HeaderHelp id="audit-score-help" label="What the score means">
                    This job&apos;s Home Health Report Card score out of 100. 75 and above is good,
                    50 to 74 is ok, and 49 or below is bad.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Date Completed</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Issues
                  <HeaderHelp id="audit-issues-help" label="What issues means" align="right">
                    Checklist fields that failed, are missing, warned, or need review. Passes and
                    fields that do not apply are not included.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Findings
                  <HeaderHelp id="audit-findings-help" label="What findings means" align="right">
                    Every checklist field the audit recorded a result for, including passes and
                    fields that do not apply.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Percentage
                  <HeaderHelp id="audit-percentage-help" label="What percentage means" align="right">
                    Issues divided by findings.
                  </HeaderHelp>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {(data.items || []).map((row) => {
              const jobUrl = serviceTitanJobUrl(row.tenant_code, row.job_id);
              const issues = row.issues_count ?? 0;
              const findings = row.findings_count ?? 0;
              const issuePercent = findings > 0 ? `${Math.round((issues / findings) * 100)}%` : '—';
              return (
              <tr
                key={row.audit_run_id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/audit/${row.audit_run_id}`)}
              >
                <td className="whitespace-nowrap px-4 py-3 text-sm">
                  {tenantDisplayName(row.tenant_code, data.tenants)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm">
                  {jobUrl ? (
                    <a
                      href={jobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.job_id}
                    </a>
                  ) : (
                    row.job_id
                  )}
                </td>
                <td className="px-4 py-2">
                  <TechnicianScoreGauge
                    score={typeof row.score === 'number' ? row.score : null}
                    labelPrefix={`Job ${row.job_id} score`}
                  />
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(row.overall_job_status)}`}>
                    {formatStatus(row.overall_job_status)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {formatAuditTime(row.created_at)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {issues}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {findings}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm font-bold text-gray-900">
                  {issuePercent}
                </td>
              </tr>
              );
            })}
            {!loading && (data.items || []).length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  No scored audit runs match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobAuditQueue;
